/**
 * Video Section player — reliable YouTube/Vimeo/Shopify click-to-play.
 * Creates the iframe inside the click handler so browsers allow unmuted autoplay.
 */
class VideoSectionPlayer extends HTMLElement {
  connectedCallback() {
    this.poster = this.querySelector('[data-vs-poster]');
    this.host = this.querySelector('[data-vs-embed]');
    if (!this.poster || !this.host) return;

    this.onPosterClick = () => this.play();
    this.poster.addEventListener('click', this.onPosterClick);

    if (this.dataset.autoplay === 'true') {
      this.play();
    }
  }

  disconnectedCallback() {
    this.poster?.removeEventListener('click', this.onPosterClick);
  }

  play() {
    if (this.hasAttribute('data-playing')) return;

    const source = this.dataset.source;
    const showControls = this.dataset.showControls === 'true';
    const loop = this.dataset.loop === 'true';

    if (source === 'none' || !source) return;

    this.setAttribute('data-playing', 'true');
    this.poster?.setAttribute('hidden', '');

    if (source === 'youtube' || source === 'vimeo') {
      this.#loadIframe(source, showControls, loop);
      return;
    }

    if (source === 'shopify') {
      this.#loadShopifyVideo(showControls, loop);
    }
  }

  #loadIframe(source, showControls, loop) {
    const id = this.dataset.videoId;
    if (!id || !this.host) return;

    const iframe = document.createElement('iframe');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
    );
    iframe.title = this.dataset.videoAlt || 'Video';
    iframe.dataset.videoType = source;

    if (source === 'youtube') {
      const params = new URLSearchParams({
        autoplay: '1',
        playsinline: '1',
        rel: '0',
        modestbranding: '1',
        enablejsapi: '1',
        controls: showControls ? '1' : '0',
      });
      if (loop) {
        params.set('loop', '1');
        params.set('playlist', id);
      }
      iframe.src = `https://www.youtube.com/embed/${id}?${params.toString()}`;
    } else {
      const params = new URLSearchParams({
        autoplay: '1',
        playsinline: '1',
        controls: showControls ? '1' : '0',
        api: '1',
      });
      if (loop) params.set('loop', '1');
      iframe.src = `https://player.vimeo.com/video/${id}?${params.toString()}`;
    }

    this.host.replaceChildren(iframe);
  }

  #loadShopifyVideo(showControls, loop) {
    const template = this.querySelector('template[data-vs-video-template]');
    const video = template?.content?.querySelector('video')?.cloneNode(true);
    if (!(video instanceof HTMLVideoElement) || !this.host) return;

    video.controls = showControls;
    video.loop = loop;
    video.playsInline = true;
    video.autoplay = true;
    this.host.replaceChildren(video);
    video.play().catch(() => {});
  }
}

if (!customElements.get('video-section-player')) {
  customElements.define('video-section-player', VideoSectionPlayer);
}
